import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ScrollReveal } from "./ScrollEffects";
import { Tilt3DCard } from "./Tilt3DCard";
import { WordReveal } from "./TextEffects";

const featureKeys = [
  { key: "personality", emoji: "💬", gradient: "from-[hsl(20,100%,60%)] to-[hsl(35,80%,65%)]", tint: "hsl(20 100% 60% / 0.04)" },
  { key: "memory", emoji: "🧠", gradient: "from-[hsl(35,80%,55%)] to-[hsl(45,90%,60%)]", tint: "hsl(35 80% 55% / 0.04)" },
  { key: "emotions", emoji: "🐾", gradient: "from-[hsl(15,80%,55%)] to-[hsl(25,90%,62%)]", tint: "hsl(15 80% 55% / 0.04)" },
  { key: "rainbow", emoji: "🌈", gradient: "from-[hsl(230,40%,65%)] to-[hsl(260,35%,70%)]", tint: "hsl(230 40% 65% / 0.04)" },
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.2 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 25, scale: 0.95 },
  visible: {
    opacity: 1, y: 0, scale: 1,
    transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as [number, number, number, number] },
  },
};

export const FeaturesSection: React.FC = () => {
  const { t } = useTranslation();

  return (
    <section
      className="py-16 sm:py-20 px-4 relative overflow-hidden"
      id="features"
      style={{ background: "hsl(25 60% 95% / 0.4)" }}
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      <div className="max-w-6xl mx-auto relative z-10">
        <ScrollReveal className="text-center mb-16">
          <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-widest mb-4">
            {t('features.section_badge')}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-foreground mb-4">
            <WordReveal
              text={`${t('features.section_title_prefix')} ${t('features.section_title_highlight')}`}
              highlightWords={t('features.section_title_highlight').split(' ')}
            />
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            {t('features.section_subtitle')}
          </p>
        </ScrollReveal>

        <motion.div
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 relative"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          variants={containerVariants}
        >
          <div
            className="hidden lg:block absolute top-[60px] left-[12.5%] right-[12.5%] h-px"
            style={{ background: "linear-gradient(90deg, transparent, hsl(var(--border)), hsl(var(--border)), transparent)", opacity: 0.4 }}
          />

          {featureKeys.map((f, i) => (
            <motion.div key={i} variants={itemVariants}>
              <Tilt3DCard className="h-full">
                <motion.div
                  className="glass rounded-3xl p-7 border border-border/50 cursor-default h-full"
                  whileHover={{ backgroundColor: f.tint }}
                  transition={{ duration: 0.3 }}
                >
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${f.gradient} flex items-center justify-center mb-5 shadow-warm-sm`}>
                    <motion.span
                      className="text-2xl"
                      whileHover={{ scale: 1.2, rotate: [0, -10, 10, 0] }}
                      transition={{ type: "spring", stiffness: 400 }}
                    >
                      {f.emoji}
                    </motion.span>
                  </div>
                  <h3 className="font-bold text-foreground text-lg mb-2">{t(`features.${f.key}.title`)}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{t(`features.${f.key}.description`)}</p>
                </motion.div>
              </Tilt3DCard>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
